import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { goToAuthorityOrMainApp, setLanguage } from '@/shared/actions';
import { getLanguage } from '@/services/GRNLwithoutDep';
import { translate } from '@/services/TranslationService';
import { NovovilleButton, Typography } from '@/components/novoville';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import '../../output.css';
import { setCameFrom } from '../home/actions';
import AuthPageContainer from './AuthPageContainter';
import { clearHelloSession, continueLogin } from './actions';

// Ensure this path is correct based on your project structure

const LoginConfirmationPage = ({ history }) => {
  const { code, email, code_expires_at } = useSelector(
    (state) => state.authReducer
  );
  const cameFromRegistration = useSelector((state) => {
    const routerFlag =
      state.router?.location?.state?.from === '/registration-page';
    return state.authReducer?.cameFromRegistration || routerFlag;
  });

  const dispatch = useDispatch();
  const submittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialLanguage = getLanguage();

  const [selectedLanguage, setSelectedLanguage] = useState(
    initialLanguage || ''
  );

  const handleLanguageChange = async (newLanguage) => {
    setSelectedLanguage(newLanguage);
    await dispatch(setLanguage(newLanguage));
  };

  const navigate = useCallback(
    (path) => {
      history.replace(path);
    },
    [history]
  );

  const sessionValid =
    !!code &&
    !!email &&
    (code_expires_at ? Date.now() < code_expires_at : true);

  const handleReset = useCallback(() => {
    dispatch(clearHelloSession());
    navigate('/hello');
  }, [dispatch, navigate]);

  useEffect(() => {
    if (!sessionValid && !isSubmitting && !submittingRef.current) {
      handleReset();
    }
  }, [sessionValid, isSubmitting, handleReset]);

  useEffect(() => {
    if (isSubmitting || !sessionValid || !code_expires_at) return;

    const remaining = code_expires_at - Date.now();
    if (remaining <= 0) {
      toast({
        title: translate('Error'),
        description: translate('Session expired. Please try again.'),
        variant: 'destructive',
      });
      handleReset();
      return;
    }

    const timeout = setTimeout(() => {
      if (submittingRef.current) return;
      toast({
        title: translate('Error'),
        description: translate('Session expired. Please try again.'),
        variant: 'destructive',
      });
      handleReset();
    }, remaining);

    return () => clearTimeout(timeout);
  }, [sessionValid, code_expires_at, isSubmitting, handleReset]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submittingRef.current) return;

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      await dispatch(continueLogin());
      dispatch(setCameFrom('login'));
      await dispatch(goToAuthorityOrMainApp());

      dispatch(clearHelloSession());

      return;
    } catch (error) {
      toast({
        title: translate('Error'),
        description: translate(
          error?.details?.user_description ||
            error?.code ||
            error?.message ||
            error
        ),
        variant: 'destructive',
      });
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <AuthPageContainer
      language={selectedLanguage}
      handleLanguageChange={handleLanguageChange}
    >
      <Card className="w-full h-full sm:p-[40px] sm:drop-shadow-md drop-shadow-none shadow-none text-gray-800 px-[24px] border-none">
        <form className="h-full flex flex-col" onSubmit={handleSubmit}>
          <CardContent className="mb-8 p-0 flex flex-col space-y-3">
            <div className="h-20 w-20 mb-5 flex justify-center items-center rounded-sm bg-gainsBoro mx-auto">
              <Mail className="text-darkBlueishGray h-8 w-8" />
            </div>
            <Typography variant={'bold_32'} className="text-center">
              {translate('Check Your Email!')}
            </Typography>
            <Typography variant={'regular_16'} className="text-center">
              {cameFromRegistration ? (
                <>
                  {translate('We have sent an email message to your address')}
                  <strong className="line-clamp-1 text-ellipsis">
                    {' '}
                    {email}.{' '}
                  </strong>
                  {translate(
                    'Please check your inbox, confirm your email and then continue.'
                  )}
                </>
              ) : (
                translate(
                  'We have sent you an email message. Please check your inbox and click on the button “Confirm”.'
                )
              )}
            </Typography>
          </CardContent>
          <CardFooter className="p-0 flex flex-col justify-center items-center mt-auto">
            {!cameFromRegistration && (
              <NovovilleButton
                type="submit"
                loading={isSubmitting}
                variant="primary"
                className="w-full mb-4"
              >
                {translate('I have done this connect me')}
              </NovovilleButton>
            )}
            {cameFromRegistration && (
              <div className="flex flex-col w-full mb-4">
                <NovovilleButton
                  type="submit"
                  loading={isSubmitting}
                  variant="primary"
                  className="w-full mb-4"
                >
                  {translate('Continue')}
                </NovovilleButton>
              </div>
            )}
            <NovovilleButton
              type="button"
              disabled={isSubmitting}
              variant="outline"
              className="w-full"
              onClick={handleReset}
            >
              {translate('alert.cancel')}
            </NovovilleButton>
          </CardFooter>
        </form>
      </Card>
    </AuthPageContainer>
  );
};

export default withRouter(LoginConfirmationPage);
